import { DateSchema, INDEPENDENCE_LEVEL_TYPE, TIME_FRAME, getFimItemSchema } from "@packages/core";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { LuArrowLeft, LuArrowRight, LuChevronLeft, LuPencil } from "react-icons/lu";
import {
  Bar,
  type BarShapeProps,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";

import {
  Breadcrumbs,
  ButtonStyle,
  PageName,
  PaperStyle,
  TypographyStyle,
  useResponsiveLineChartStyles,
} from "#components";
import { useIndependenceLevelEntryInfiniteQuery, useResident, useTranslate } from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/residents/$userKey/independence-levels/$fimItem",
)({
  component: Component,
  params: {
    parse(params) {
      return v.parse(
        v.object({
          fimItem: v.pipe(
            v.string(),
            v.transform(function (input) {
              return Number.parseInt(input);
            }),
            getFimItemSchema(),
          ),
        }),
        params,
      );
    },
  },
  validateSearch: v.object({
    date: v.fallback(v.optional(DateSchema()), undefined),
    numberOfDays: v.fallback(v.optional(v.pipe(v.number(), v.integer(), v.minValue(7))), undefined),
  }),
});

function Component() {
  const { facilityKey, fimItem, userKey } = Route.useParams();
  const { date: _date, numberOfDays = 7 } = Route.useSearch();

  const resident = useResident(userKey);
  const router = useRouter();
  const translate = useTranslate();

  const date = useMemo(
    function () {
      return _date ?? Temporal.Now.plainDateISO("Asia/Tokyo");
    },
    [_date],
  );

  const independenceLevelEntryInfiniteQuery = useIndependenceLevelEntryInfiniteQuery({
    condition: [
      ["userKey", "=", userKey],
      [
        "assessedAt",
        "BETWEEN",
        date.subtract({ days: numberOfDays }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
        "AND",
        date.add({ days: 2 }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
      ],
    ],
  });

  const independenceLevelEntries = useMemo(
    function () {
      return (
        independenceLevelEntryInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.independenceLevelEntries;
        }) ?? []
      );
    },
    [independenceLevelEntryInfiniteQuery.data],
  );

  const data = useMemo(
    function () {
      return independenceLevelEntries.map(function (independenceLevelEntry) {
        return {
          independenceLevelEntry,
          x: independenceLevelEntry.assessedAt.toZonedDateTime("Asia/Tokyo").epochMilliseconds,
          y: independenceLevelEntry.independenceLevels[fimItem],
          ys: [
            independenceLevelEntry.independenceLevels[fimItem] - 1,
            independenceLevelEntry.independenceLevels[fimItem] + 1,
          ],
        };
      });
    },
    [independenceLevelEntries, fimItem],
  );

  const ticks = useMemo(
    function () {
      const start = date.subtract({ days: numberOfDays - 1 });
      const end = date.add({ days: 1 });
      return Array.from({ length: start.until(end).days + 1 }, function (_, i) {
        return start.add({ days: i }).toZonedDateTime("Asia/Tokyo").epochMilliseconds;
      }).filter(function (_, index, { length }) {
        return numberOfDays > 7
          ? index === 0 || index === length - 1 || index % Math.round(length / 4) === 0
          : true;
      });
    },
    [date, numberOfDays],
  );

  const styles = useResponsiveLineChartStyles();

  return (
    <main
      className={css({
        paddingLeft: { smDown: "0 !important" },
        paddingRight: { smDown: "0 !important" },
      })}
    >
      <PageName>
        {translate("%FimItem." + fimItem + ".0%") +
          " - " +
          (resident?.name ?? translate("%Loading.0%"))}
      </PageName>

      <div
        className={css({
          alignItems: "center",
          display: "flex",
          gap: "16px",
          justifyContent: "space-between",
        })}
      >
        <Link
          className={css({ alignItems: "center", display: "flex", gap: "4px", hideFrom: "sm" })}
          onClick={function (event) {
            if (router.history.canGoBack()) {
              event.preventDefault();
              event.stopPropagation();
              router.history.back();
            }
          }}
          params={{ facilityKey, userKey }}
          to="/facilities/$facilityKey/residents/$userKey"
        >
          <LuChevronLeft className={css({ height: "32px", width: "auto" })} />
          <p
            className={TypographyStyle({
              color: "primary",
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: 1,
            })}
          >
            {translate("%HonorificTitle.0%", {
              $: resident ?? { name: translate("%Resident.0%") },
            })}
          </p>
        </Link>

        <Breadcrumbs
          className={css(
            TypographyStyle.raw({ fontSize: "14px", fontWeight: 500, lineHeight: 1 }),
            { display: "flex", hideBelow: "sm" },
          )}
        >
          <Link
            className={css({ color: "#71717A" })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey"
          >
            {translate("%ResidentList.0%")}
          </Link>
          <Link
            className={css({ color: "#71717A" })}
            params={{ facilityKey, userKey }}
            to="/facilities/$facilityKey/residents/$userKey"
          >
            {translate("%HonorificTitle.0%", {
              $: resident ?? { name: translate("%Resident.0%") },
            })}
          </Link>
          <p>{translate("%FimItem." + fimItem + ".0%")}</p>
        </Breadcrumbs>

        <Link
          className={css({ alignItems: "center", display: "flex", gap: "8px", padding: "8px" })}
          params={{ facilityKey, userKey }}
          search={{ date, fimItem }}
          to="/facilities/$facilityKey/residents/$userKey/independence-levels/registration"
        >
          <LuPencil />
          <p className={TypographyStyle({ fontSize: "14px", fontWeight: 500, lineHeight: 1 })}>
            {translate("%Edit.0%")}
          </p>
        </Link>
      </div>

      <div
        className={css({
          backgroundColor: "white",
          borderRadius: { sm: "12px" },
          marginTop: "16px",
          padding: "16px",
        })}
      >
        <h3
          className={css(TypographyStyle.raw({ lineHeight: 1 }), {
            fontSize: { base: "16px", sm: "20px" },
            fontWeight: { base: 500, sm: 700 },
            textAlign: { base: "center", sm: "left" },
          })}
        >
          {translate("%FimItem." + fimItem + ".0%")}
        </h3>

        <div
          className={css({
            backgroundColor: "muted-background",
            borderRadius: "6px",
            display: "flex",
            marginTop: "12px",
            padding: "4px",
            width: "100%",
          })}
        >
          {[7, 30].map(function (option, index) {
            return (
              <Link
                className={css(
                  TypographyStyle.raw({
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: 1,
                    textAlign: "center",
                  }),
                  {
                    borderRadius: "2px",
                    display: "block",
                    flexGrow: 1,
                    paddingX: "12px",
                    paddingY: "6px",
                    _selected: { backgroundColor: "background" },
                  },
                )}
                data-selected={numberOfDays === option || undefined}
                key={index}
                params={{ facilityKey, fimItem, userKey }}
                replace
                search={{ date, numberOfDays: option }}
                to="."
              >
                {option === 7
                  ? translate("%Week.0%")
                  : option === 30
                    ? translate("%Month.0%")
                    : translate("%NumberOfDays.0%", { $: { numberOfDays: option } })}
              </Link>
            );
          })}
        </div>

        <p
          className={css(TypographyStyle.raw({ color: "primary", fontSize: "14px" }), {
            marginTop: "12px",
            opacity: 0.6,
          })}
        >
          {translate("%Date.1%", { $: date.subtract({ days: numberOfDays - 1 }) })}〜
          {translate("%Date.1%", { $: date })}
        </p>

        {independenceLevelEntries.length === 0 && (
          <p className={TypographyStyle({ color: "primary", fontSize: "36px", fontWeight: 700 })}>
            {translate("%NoData.1%")}
          </p>
        )}

        <div
          className={css({
            alignItems: "center",
            display: "flex",
            gap: "16px",
            justifyContent: "center",
          })}
        >
          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "outlined",
                borderRadius: "100%",
                color: "outlined",
                icon: true,
                size: "32px",
              }),
              { hideBelow: "sm" },
            )}
            search={{ date: date.subtract({ days: numberOfDays }), numberOfDays }}
            to="."
          >
            <LuArrowLeft className={css({ height: "16px", width: "auto" })} />
          </Link>

          <ResponsiveContainer height={420} width="100%">
            <ComposedChart data={data}>
              <XAxis
                allowDataOverflow
                axisLine={false}
                dataKey="x"
                tick={{ fill: "#18181B4D", fontSize: "12px" }}
                tickFormatter={function (epoch) {
                  return numberOfDays > 7
                    ? translate("%Day.1%", {
                        $: Temporal.Instant.fromEpochMilliseconds(epoch).toZonedDateTimeISO(
                          "Asia/Tokyo",
                        ),
                      })
                    : translate(
                        "%DayOfWeek." +
                          Temporal.Instant.fromEpochMilliseconds(epoch).toZonedDateTimeISO(
                            "Asia/Tokyo",
                          ).dayOfWeek +
                          ".0%",
                      );
                }}
                tickLine={false}
                ticks={ticks}
                type="number"
              />
              <YAxis
                axisLine={false}
                dataKey="y"
                domain={[1, 7]}
                padding={{ top: 44, bottom: 44 }}
                tick={{ fill: "#18181B4D", fontSize: "12px" }}
                tickFormatter={function (independenceLevel) {
                  return translate("%IndependenceLevel." + independenceLevel + ".0%");
                }}
                tickLine={false}
                ticks={[1, 2, 3, 4, 5, 6, 7]}
                type="number"
                width={72}
              />
              <Legend
                content={function () {
                  return (
                    <div
                      className={css({ display: "flex", gap: "24px", justifyContent: "center" })}
                    >
                      <div className={css({ alignItems: "center", display: "flex", gap: "4px" })}>
                        <div
                          className={css({
                            aspectRatio: "1 / 1",
                            backgroundColor: "white",
                            borderColor: "#DB2777",
                            borderRadius: "100%",
                            borderStyle: "solid",
                            borderWidth: "3px",
                            height: "12px",
                          })}
                        />
                        <p className={TypographyStyle({ color: "primary/60", fontSize: "12px" })}>
                          {translate(
                            "%AssessmentType." + INDEPENDENCE_LEVEL_TYPE.ESTIMATED + ".1%",
                          )}
                        </p>
                      </div>
                      <div className={css({ alignItems: "center", display: "flex", gap: "4px" })}>
                        <div
                          className={css({
                            aspectRatio: "1 / 1",
                            backgroundColor: "#DB277733",
                            borderRadius: "100%",
                            height: "12px",
                          })}
                        />
                        <p className={TypographyStyle({ color: "primary/60", fontSize: "12px" })}>
                          {translate("%Range.0%")}
                        </p>
                      </div>
                    </div>
                  );
                }}
              />
              <Tooltip
                content={function ({ payload }) {
                  const independenceLevelEntry: (typeof data)[number]["independenceLevelEntry"] =
                    payload.at(0)?.payload.independenceLevelEntry;
                  if (!independenceLevelEntry) {
                    return null;
                  }
                  return (
                    <div
                      className={css(PaperStyle.raw({ borderRadius: "12px" }), {
                        backgroundColor: "#FFFFFFCC",
                        padding: "16px",
                      })}
                    >
                      <p className={TypographyStyle({ color: "primary/60", fontSize: "12px" })}>
                        {translate("%DateTime.1%", { $: independenceLevelEntry.assessedAt })}
                      </p>
                      <p>
                        <span className={TypographyStyle({ color: "primary", fontSize: "12px" })}>
                          {translate("%IndependenceLevel.0%")}
                        </span>
                        &nbsp;
                        <span
                          className={TypographyStyle({
                            color: "primary",
                            fontSize: "14px",
                            fontWeight: 700,
                          })}
                        >
                          {independenceLevelEntry.independenceLevels[fimItem]}
                        </span>
                        &nbsp;
                        <span className={TypographyStyle({ color: "primary", fontSize: "12px" })}>
                          ({translate("%AssessmentType." + independenceLevelEntry.type + ".0%")})
                        </span>
                      </p>
                    </div>
                  );
                }}
              />
              <CartesianGrid stroke="#E4E4E7" syncWithTicks vertical={false} />
              <Line
                activeDot={styles.lineDot}
                dataKey="y"
                dot={styles.lineDot}
                stroke="#DB2777"
                strokeWidth={styles.lineStrokeWidth}
              />
              <Bar
                barSize={0}
                dataKey="ys"
                shape={function ({ height, x, y }: BarShapeProps) {
                  if (
                    typeof height !== "number" ||
                    typeof x !== "number" ||
                    typeof y !== "number"
                  ) {
                    return <path />;
                  }

                  return (
                    <Rectangle
                      fill="#DB277733"
                      height={height}
                      radius={styles.barRadius}
                      width={styles.barSize}
                      x={x - styles.barSize / 2}
                      y={y}
                    />
                  );
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>

          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "outlined",
                borderRadius: "100%",
                color: "outlined",
                icon: true,
                size: "32px",
              }),
              { hideBelow: "sm" },
            )}
            search={{ date: date.add({ days: numberOfDays }), numberOfDays }}
            to="."
          >
            <LuArrowRight className={css({ height: "16px", width: "auto" })} />
          </Link>
        </div>
      </div>
    </main>
  );
}
